import React, {Component} from 'react';
import {Link, Navigate} from 'react-router-dom';
import {withTranslation} from 'react-i18next';
import {APIClient} from '../../utils/APIClient';
import {API_URL} from '../../settings/local_settings';
import {AccessService} from '../../utils/AccessService';
import {useFicha21} from '../../context/UserModulesContext';
import {Spinner} from '../Spinner';
import {ClientHeader} from './ClientHeader';
import {ClientTabs} from './ClientTabs';
import {ClientAgentArea} from './agent/ClientAgentArea';
import {ProjectInstructionsBlock} from './ia_assistant/ProjectInstructionsBlock';
import {IAContextMeter} from './ia_assistant/IAContextMeter';
import {PromptLibrarySection} from './ia_assistant/PromptLibrarySection';
import {copyStatusAfterCopy, fetchPromptCopyStatus} from '../prompts/promptCopyPing';
import {BoosterStage} from './ia_booster/BoosterStage';
import {BOOSTER_STEP_USE_IA, boosterFlowPathForView, boosterStepsFor} from './ia_booster/boosterSteps';

// Tab "IA" unificado (ficha v2, D7 del SSOT de UX): los tres modos de usar IA con este cliente
// en un solo lugar — Dashbito (chat in-product), Conectar tu IA (MCP externo, checklist) y
// Prompts (biblioteca por momentos). Sub-vistas por URL (/client/:id/ia/:view), gateadas por
// módulo: dashbito ← AGENT, connect/prompts ← DASHBO_MCP.
class ClientIATabContentClass extends Component {
    constructor(props) {
        super(props);
        this.state = {
            name: '',
            businessUnits: null,
            clientAdAccounts: null,
            starterPack: null,
            starterPackFailed: false,
            // Estado "qué versión copió la agencia PARA este cliente" (chip de versión).
            copyStatus: {},
        };
        this.handleRetryModules = this.handleRetryModules.bind(this);
        this.handlePromptCopied = this.handlePromptCopied.bind(this);
    }

    // Los módulos los sirve el provider, así que reintentar es re-fetchear ahí (no recargar la app
    // entera). Sigue existiendo porque "no pude leer los módulos" ≠ "no tenés el módulo": sin esta
    // pantalla de error, un blip de red expulsaba del tab a usuarios que sí tienen acceso.
    handleRetryModules() {
        this.props.refreshModules();
    }

    componentDidMount() {
        this.fetchClientData();
        // El provider puede tener los módulos ya resueltos en el primer render (cache local): en
        // ese caso `componentDidUpdate` no llega a dispararse nunca.
        this.maybeFetchStarterPack();
    }

    fetchClientData() {
        new APIClient(API_URL + 'client/' + this.props.clientId).call('GET').then((data) => {
            if (data && data.status === 'OK') {
                this.setState({
                    name: data.data.name,
                    businessUnits: data.data.business_units || [],
                    clientAdAccounts: data.data.client_ad_accounts || [],
                });
            }
        });
    }

    // El starter pack depende del módulo MCP, que llega por el provider (asíncrono): se pide en
    // cuanto los módulos están y una sola vez.
    componentDidUpdate(prevProps) {
        if (!prevProps.modules && this.props.modules) this.maybeFetchStarterPack();
    }

    maybeFetchStarterPack() {
        if (this._starterPackRequested || !this.props.modules || !this.hasMcpModule()) return;
        this._starterPackRequested = true;
        this.fetchStarterPack();
    }

    fetchStarterPack() {
        const url = `${API_URL}agent/client_starter_pack/?agency_client_id=${this.props.clientId}`;
        new APIClient(url).call('GET').then((data) => {
            if (data && data.instructions_markdown !== undefined) {
                this.setState({starterPack: data});
            } else {
                this.setState({starterPackFailed: true});
            }
        });
        fetchPromptCopyStatus({agencyClientId: this.props.clientId})
            .then((copyStatus) => this.setState({copyStatus}));
    }

    // Actualización optimista tras copiar (helper único — las reglas viven con él).
    handlePromptCopied(promptId, version) {
        this.setState((state) => ({
            copyStatus: copyStatusAfterCopy(state.copyStatus, promptId, version),
        }));
    }

    hasAgentModule() {
        return (this.props.modules || []).includes(AccessService.MODULE_AGENT);
    }

    hasMcpModule() {
        return (this.props.modules || []).includes(AccessService.MODULE_DASHBO_MCP);
    }

    subViews() {
        const views = [];
        if (this.hasAgentModule()) views.push('dashbito');
        // 'connect' aparece también sin módulo MCP (muestra el upsell de plan, D7): el tab IA
        // existe con cualquiera de los dos módulos y la conexión externa es el pitch a vender.
        if (this.hasAgentModule() || this.hasMcpModule()) views.push('connect');
        if (this.hasMcpModule()) views.push('prompts');
        return views;
    }

    activeView() {
        const views = this.subViews();
        return views.includes(this.props.view) ? this.props.view : views[0];
    }

    renderSubNav() {
        const {t} = this.props;
        const active = this.activeView();
        return (
            <nav className="cmem-subnav ia-tab-subnav">
                {this.subViews().map((view) => (
                    <Link
                        key={view}
                        className={`cmem-pill ${view === active ? '-active' : ''}`}
                        to={`/client/${this.props.clientId}/ia/${view}`}
                    >
                        {t(`ia_tab.subnav.${view}`)}
                    </Link>
                ))}
            </nav>
        );
    }

    renderConnect() {
        const {t} = this.props;
        if (!this.hasMcpModule()) {
            // Upsell de plan (nudge `plan`, D7): la agencia tiene Dashbito pero no el módulo MCP.
            return (
                <div className="ia-connect-upsell">
                    <p>{t('ia_tab.connect.upsell_text')}</p>
                </div>
            );
        }
        const pack = this.state.starterPack;
        const mcpUsed = !!(pack && pack.mcp_recently_used);
        return (
            <div className="ia-connect">
                <div className="ia-connect-main">
                    <div className="ia-step">
                        <div className={`ia-step-num ${mcpUsed ? '-done' : ''}`}>
                            {mcpUsed ? <i className="fas fa-check"/> : '1'}
                        </div>
                        <div>
                            <h5>{t('ia_tab.connect.step1_title')}</h5>
                            <p>{mcpUsed
                                ? t('ia_tab.connect.step1_done')
                                : t('ia_tab.connect.step1_pending')}</p>
                            {!mcpUsed && (
                                <Link className="btn -alt -negative" to="/dashbo-mcp">
                                    {t('ia_tab.connect.step1_guide_cta')}
                                </Link>
                            )}
                        </div>
                    </div>
                    <div className="ia-step">
                        <div className="ia-step-num">2</div>
                        <div className="ia-step-body">
                            <h5>{t('ia_tab.connect.step2_title')}</h5>
                            <p>{t('ia_tab.connect.step2_text')}</p>
                            {pack && (
                                <ProjectInstructionsBlock
                                    minimalMarkdown={pack.minimal_markdown}
                                    fullMarkdown={pack.instructions_markdown}
                                    minimalPrompt={pack.minimal_prompt}
                                    instructionsPrompt={pack.instructions_prompt}
                                    copyStatus={this.state.copyStatus}
                                    onCopied={this.handlePromptCopied}
                                    clientId={this.props.clientId}
                                />
                            )}
                            {!pack && !this.state.starterPackFailed && (
                                <div className="box-loader"><Spinner color="positive small"/></div>
                            )}
                            {this.state.starterPackFailed && (
                                <p className="ia-connect-error">{t('ia_assistant.error_generic')}</p>
                            )}
                        </div>
                    </div>
                    <div className="ia-step">
                        <div className="ia-step-num">3</div>
                        <div>
                            <h5>{t('ia_tab.connect.step3_title')}</h5>
                            <p>{t('ia_tab.connect.step3_text')}</p>
                            <Link className="btn -alt -negative" to={`/client/${this.props.clientId}/ia/prompts`}>
                                {t('ia_tab.connect.step3_cta')}
                            </Link>
                        </div>
                    </div>
                </div>
                <div className="ia-connect-side">
                    <IAContextMeter clientId={this.props.clientId}/>
                </div>
            </div>
        );
    }

    // El cierre del Recorrido. Todo lo que sigue a este punto ya está gateado por `ficha21` (la
    // pantalla redirige antes si no), así que acá no hace falta volver a chequearlo.
    //
    // Con la one-page (card LDdcpf52) esta pantalla sobrevive SÓLO para Dashbito: `connect` y
    // `prompts` redirigen al Recorrido antes de llegar acá, así que el método sigue vivo pero lo
    // único que dibuja es el hilo —la navegación de vuelta a los actos—. La banda «Usa tu IA» se
    // retiró: su única condición era `!isDashbito`, que ya no ocurre nunca, y ahora la dibuja la
    // one-page (`flow/BoosterFlowContent`). Sobre el chat nunca se dibujó: ocupa el alto útil de la
    // pantalla y ahí "usar tu IA" ES la vista, no algo que haya que anunciar.
    renderBoosterClose() {
        const steps = boosterStepsFor({
            clientMemoryLevel: this.props.clientMemoryLevel,
            modules: this.props.modules,
        });
        return (
            <BoosterStage clientId={this.props.clientId} clientName={this.state.name}
                          step={BOOSTER_STEP_USE_IA} steps={steps}/>
        );
    }

    renderActiveView() {
        const active = this.activeView();
        if (active === 'dashbito') {
            return (
                <ClientAgentArea
                    clientId={this.props.clientId}
                    sessionId={this.props.sessionId}
                    adAccounts={this.state.clientAdAccounts}
                    businessUnits={this.state.businessUnits}
                    sessionBasePath={`/client/${this.props.clientId}/ia/dashbito`}
                />
            );
        }
        if (active === 'connect') {
            return this.renderConnect();
        }
        if (active === 'prompts') {
            return <PromptLibrarySection clientId={this.props.clientId} groupByMoment/>;
        }
        return null;
    }

    render() {
        const {t} = this.props;
        const isDashbito = this.activeView() === 'dashbito';
        if (this.props.modulesLoaded && !this.props.modules) {
            return (
                <section className="section-content">
                    <ClientHeader name={this.state.name}/>
                    <ClientTabs clientId={this.props.clientId}/>
                    <div className="section-body">
                        <div className="box ia-tab-error">
                            <p>{t('ia_tab.load_error')}</p>
                            <button className="btn -alt" onClick={this.handleRetryModules}>
                                {t('ia_tab.retry')}
                            </button>
                        </div>
                    </div>
                </section>
            );
        }
        if (!this.props.modulesLoaded) {
            return (
                <section className="section-content">
                    <ClientHeader name={this.state.name}/>
                    <ClientTabs clientId={this.props.clientId}/>
                    <div className="section-body">
                        <div className="box-loader"><Spinner color="positive"/></div>
                    </div>
                </section>
            );
        }

        // Sin flag o sin ningún módulo de IA: el tab no existe — a las rutas legacy o afuera.
        if (!this.props.ficha21) {
            if (this.hasAgentModule()) {
                // Preservar la sesión deep-linkeada (la ruta legacy la soporta).
                const suffix = this.props.sessionId ? `/${this.props.sessionId}` : '';
                return <Navigate to={`/client/${this.props.clientId}/agent${suffix}`} replace/>;
            }
            return <Navigate to={`/client/${this.props.clientId}/ia-assistant`} replace/>;
        }
        if (this.subViews().length === 0) {
            return <Navigate to={`/client/${this.props.clientId}/monitoring`} replace/>;
        }
        // El Recorrido se lleva `connect` (→ `#usa`) y `prompts` (→ `#prompts`); el chat NO migra
        // —ocupa el alto útil y no puede vivir dentro de un scroll—, así que
        // `boosterFlowPathForView` devuelve `null` para `dashbito` y el deep-link de sesión
        // (`/client/:id/ia/dashbito/:sessionId`) sigue entrando acá intacto.
        //
        // Después del guard de `subViews()` vacío por el mismo motivo que en Memoria: sin ningún
        // módulo de IA `activeView()` es `undefined` y el destino saldría sin ancla, en vez de
        // sacar al usuario del tab. La query viaja entera (`?connect=…` y compañía).
        if (this.props.ficha21) {
            const to = boosterFlowPathForView(
                this.activeView(), this.props.clientId, window.location.search);
            if (to) return <Navigate to={to} replace/>;
        }

        return (
            <section className={`section-content ${isDashbito ? 'agent-area-wrapper' : ''}`}>
                <ClientHeader name={this.state.name}/>
                <ClientTabs clientId={this.props.clientId}/>
                {this.renderBoosterClose()}
                <div className={isDashbito ? 'ia-tab-agent' : 'section-body'}>
                    {isDashbito
                        ? (
                            <>
                                {this.renderSubNav()}
                                {this.renderActiveView()}
                            </>
                        )
                        : (
                            <div className="box">
                                <header className="box-header">
                                    <h2 className="box-title">{this.props.t('ia_tab.title')}</h2>
                                </header>
                                {this.renderSubNav()}
                                <div className="ia-tab-view">
                                    {this.renderActiveView()}
                                </div>
                            </div>
                        )}
                </div>
            </section>
        );
    }
}

const ClientIATabContentInner = withTranslation()(ClientIATabContentClass);

// Módulos y flag del rediseño salen del provider. Antes esta pantalla hacía DOS fetches del mismo
// endpoint (uno para los módulos y otro para el flag).
export function ClientIATabContent(props) {
    // `clientMemoryLevel` no lo usa esta pantalla para gatearse: lo necesita el hilo del Recorrido,
    // que dibuja los MISMOS pasos que el sidebar y por eso pide los mismos datos de acceso.
    const {ficha21, clientMemoryLevel, modules, loaded, refresh} = useFicha21();
    return <ClientIATabContentInner {...props} ficha21={ficha21} modules={modules}
                                    clientMemoryLevel={clientMemoryLevel}
                                    modulesLoaded={loaded} refreshModules={refresh}/>;
}
